import JsZip from 'jszip'
import iconv from 'iconv-lite'
import { getFileMd5 } from '../../helper'
import { IFileList } from '@/types/placeOrderTypes'

// @ts-ignore
import Oss from 'ali-oss'
import { getOssInfoAES } from '@/api/oss'
import {
  creatOrder,
  editOrder,
  getMaterialFile,
  creatMaterialFile,
  creatMaterialFileSymlink,
  setUploadHistory,
} from '@/api/placeOrder'
// @ts-ignore
import { BlobReader, ZipReader } from '@zip.js/zip.js'

interface IFileExtractOptions {
  serviceContent: number // 服务内容类型
  activeNames?: string[] // 活动文件名称列表
  isPpt2Seewo?: boolean // 是否为PPT转希沃服务
}

export class FileProcessor {
  /**
   * 处理文件列表，将文件分类到不同的列表中
   */
  static async processFiles(
    files: any[],
    options: IFileExtractOptions,
  ): Promise<{
    zipList: IFileList[]
    pdfList: IFileList[]
    wordList: IFileList[]
    jsonList: IFileList[]
    imgList: IFileList[]
    activeNames: string[]
  }> {
    const result = {
      zipList: [] as IFileList[],
      pdfList: [] as IFileList[],
      wordList: [] as IFileList[],
      jsonList: [] as IFileList[],
      imgList: [] as IFileList[],
      activeNames: options.activeNames || [],
    }

    for (const item of files) {
      if (item.zip) {
        await this.processZipFile(item, result, options)
      }
      if (item.pdf) {
        await this.processPdfFile(item, result)
      }
      if (item.img) {
        await this.processImgFile(item, result)
      }
      if (item.word) {
        await this.processWordFile(item, result)
      }
      if (item.json) {
        await this.processJsonFile(item, result)
      }
    }

    return result
  }

  /**
   * 处理ZIP文件
   */
  private static async processZipFile(item: any, result: any, options: IFileExtractOptions): Promise<void> {
    if (options.isPpt2Seewo && this.hasRar7z(item?.zip?.name)) {
      // 特殊处理RAR和7Z文件 (PPT转希沃服务)
      item.status = 'check-success'
      item.checkInfo = '检查通过'
      setTimeout(() => {
        item.status = 'init'
        result.activeNames.push(item.zip!.name)
      }, 1000)
      item.pptList = [{ name: '新建 PPT 演示文稿.ppt' }]

      if (!item.md5) {
        item.md5 = (await getFileMd5(item.zip as File)) as string
      }

      result.zipList.push(item)
    } else if (item.status === 'checking') {
      result.zipList.push(item)

      // 检查文件大小
      if ((item.zip as File).size > 1024 * 1024 * 1024 * 4) {
        item.status = 'check-fail'
        item.checkInfo = '文件大小超过4G，请拆分后上传'
        return
      }

      // 大于1G小于4G
      else if (item.zip.size > 1024 * 1024 * 1024) {
        try {
          await this.examineBigZipContents(item, options, result.activeNames)
        } catch (error) {
          // 处理损坏的ZIP文件
          item.status = 'check-fail'
          item.checkInfo = 'ZIP文件无法打开，请检查后重新上传'
        }
      }

      try {
        await this.examineZipContents(item, options, result.activeNames)
      } catch (error) {
        // 处理损坏的ZIP文件
        item.status = 'check-fail'
        item.checkInfo = 'ZIP文件无法打开，请检查后重新上传'
      }
    } else {
      result.zipList.push(item)
    }
  }

  // 处理大文件zip
  private static async examineBigZipContents(
    item: any,
    options: IFileExtractOptions,
    activeNames: string[],
  ): Promise<void> {
    try {
      // 使用 zip.js 的 BlobReader 和 ZipReader 处理大文件
      const blobReader = new BlobReader(item.zip as File)
      const zipReader = new ZipReader(blobReader)

      // 获取 zip 文件中的所有条目
      const entries = await zipReader.getEntries()

      const imageList: any[] = []
      const fbdList: any[] = []
      const pptList: any[] = []
      const ZIPinZIPList: any[] = []

      // 遍历所有条目
      for (const entry of entries) {
        if (!entry.directory) {
          // 解码文件名（处理中文编码问题）
          let fileName = entry.filename
          const fileExtension = this.getFileExtension(fileName).toLowerCase()

          // 创建文件对象
          const fileObj = {
            name: fileName || '',
            size: entry.uncompressedSize,
            type: fileExtension,
            path: fileName || '',
            entry: entry, // 保存 zip.js 的 entry 对象以便后续提取
          }

          // 根据文件类型分类
          if (['ppt', 'pptx'].includes(fileExtension)) {
            pptList.push(fileObj)
          } else if (['zip', 'rar', '7z'].includes(fileExtension)) {
            ZIPinZIPList.push(fileObj)
          } else if (['fbd'].includes(fileExtension)) {
            fbdList.push(fileObj)
          } else if (['jpg', 'png', 'jpeg', 'tif', 'eps'].includes(fileExtension)) {
            imageList.push(fileObj)
          }
        }
      }

      // 关闭 zip reader
      await zipReader.close()

      // 更新文件项信息（与 examineZipContents 保持一致）
      item.fbdList = fbdList
      item.ZIPinZIPList = ZIPinZIPList
      item.pptList = pptList
      item.imageList = imageList

      if (!item.md5) {
        item.md5 = (await getFileMd5(item.zip as File)) as string
      }

      if (item.status === 'checking') {
        // 执行各种验证规则
        this.applyValidationRules(item, options, entries, fbdList, pptList, ZIPinZIPList, activeNames)
      }
    } catch (error) {
      console.error('Error processing large zip file:', error)
      item.status = 'check-fail'
      item.checkInfo = 'ZIP文件处理失败，请检查文件完整性后重新上传'
      throw error
    }
  }

  /**
   * 检查ZIP文件内容
   */
  private static async examineZipContents(
    item: any,
    options: IFileExtractOptions,
    activeNames: string[],
  ): Promise<void> {
    const zip = new JsZip()
    await zip
      .loadAsync(
        item.zip as File,
        {
          decodeFileName: (bytes: any) => {
            return iconv.decode(bytes, 'gbk')
          },
        } as any,
      )
      .then(async (file: any) => {
        const fileList = Object.values(file.files)

        // 提取各种类型的文件列表
        const fbdList = this.extractFilesByExtension(fileList, ['fbd'])
        const pptList = this.extractFilesByExtension(fileList, ['ppt', 'pptx'])
        const ZIPinZIPList = this.extractFilesByExtension(fileList, ['zip', 'rar', '7z'])
        const imageList = this.extractFilesByExtension(fileList, ['jpg', 'png', 'jpeg', 'tif', 'eps'])

        // 更新文件项信息
        item.fbdList = fbdList
        item.ZIPinZIPList = ZIPinZIPList
        item.pptList = pptList
        item.imageList = imageList

        if (!item.md5) {
          item.md5 = (await getFileMd5(item.zip as File)) as string
        }

        if (item.status === 'checking') {
          // 执行各种验证规则
          this.applyValidationRules(item, options, fileList, fbdList, pptList, ZIPinZIPList, activeNames)
        }
      })
  }

  /**
   * 应用验证规则
   */
  private static applyValidationRules(
    item: any,
    options: IFileExtractOptions,
    fileList: any[],
    fbdList: any[],
    pptList: any[],
    ZIPinZIPList: any[],
    activeNames: string[],
  ): void {
    const { serviceContent } = options

    // PPT转希沃规则
    if ([25].includes(serviceContent)) {
      if (!pptList.length) {
        item.status = 'check-fail'
        item.checkInfo = '无PPT文件'
        return
      }

      if (ZIPinZIPList.length) {
        item.status = 'check-fail'
        item.checkInfo = '压缩包不规范，压缩包内存在嵌套压缩包'
        return
      }
    }

    // FBD文件检测
    if (![25].includes(serviceContent) && !fbdList.length) {
      item.status = 'check-fail'
      item.checkInfo = '无FBD文件'
      return
    }

    // 学转教文件名规则
    if (
      [10].includes(serviceContent) &&
      fbdList.some(
        (item: any) =>
          item.name.includes(' ') ||
          item.name.includes('(') ||
          item.name.includes(')') ||
          item.name.includes('（') ||
          item.name.includes('）'),
      )
    ) {
      item.status = 'check-fail'
      item.checkInfo = '文件夹或者FBD文件名中不允许包含空格和括号'
      return
    }

    // 素材文件检测
    if (![10, 25].includes(serviceContent) && item.status !== 'check-fail') {
      const errorNoMaterial = !fileList.some(
        (item: any) =>
          this.getFileExtension(item.name).toLowerCase() === 'tif' ||
          this.getFileExtension(item.name).toLowerCase() === 'tiff' ||
          this.getFileExtension(item.name).toLowerCase() === 'eps',
      )

      if (errorNoMaterial) {
        item.status = 'check-warning'
        item.checkInfo = '项目包内不含素材文件'
      }
    }

    // PRO文件对应检测
    if (![10, 25].includes(serviceContent) && item.status !== 'check-fail') {
      const proList = this.extractFilesByExtension(fileList, ['pro']).map((item: any) => item.name)

      for (let i = 0; i < fbdList.length; i++) {
        const name = fbdList[i].name.split('.')[0]
        const hasPro = proList.some((proName: string) => {
          return proName.split('.')[0] === name
        })

        if (hasPro) {
          item.status = 'check-success'
          item.checkInfo = '检查通过'
          setTimeout(() => {
            item.status = 'init'
            activeNames.push(item.zip!.name)
          }, 1000)
          break
        } else {
          item.status = 'check-warning'
          item.checkInfo = '没有与FBD文件对应的PRO文件'
        }
      }
    }

    if (item.status !== 'check-fail') {
      item.status = 'check-success'
      item.checkInfo = '检查通过'
      setTimeout(() => {
        item.status = 'init'
        activeNames.push(item.zip!.name)
      }, 1000)
    }
  }

  /**
   * 处理PDF文件
   */
  private static async processPdfFile(item: any, result: any): Promise<void> {
    if (item.status === 'checking') {
      result.pdfList.push(item)
      if (!item.md5) {
        item.md5 = (await getFileMd5(item.pdf as File)) as string
        item.status = 'init'
      }
    } else {
      result.pdfList.push(item)
    }
  }

  /**
   * 处理图片文件
   */
  private static async processImgFile(item: any, result: any): Promise<void> {
    if (item.status === 'checking') {
      result.imgList.push(item)
      if (!item.md5) {
        item.md5 = (await getFileMd5(item.img as File)) as string
        item.status = 'init'
      }
    } else {
      result.imgList.push(item)
    }
  }

  /**
   * 处理Word文件
   */
  private static async processWordFile(item: any, result: any): Promise<void> {
    if (item.status === 'checking') {
      result.wordList.push(item)
      if (!item.md5) {
        item.md5 = (await getFileMd5(item.word as File)) as string
        item.status = 'init'
      }
    } else {
      result.wordList.push(item)
    }
  }

  /**
   * 处理JSON文件
   */
  private static async processJsonFile(item: any, result: any): Promise<void> {
    if (item.status === 'checking') {
      result.jsonList.push(item)
      if (!item.md5) {
        item.md5 = (await getFileMd5(item.json as File)) as string
        item.status = 'init'
      }
    } else {
      result.jsonList.push(item)
    }
  }

  /**
   * 从文件列表中提取指定扩展名的文件
   */
  private static extractFilesByExtension(fileList: any[], extensions: string[]): any[] {
    return fileList
      .filter((item: any) => extensions.includes(this.getFileExtension(item.name).toLowerCase()))
      .map(item => {
        const nameArr = (item as any).name.split('.')
        nameArr[nameArr.length - 1] = nameArr[nameArr.length - 1].toLowerCase()
        return { name: nameArr.join('.') }
      })
      .sort((a: any, b: any) => a.name.localeCompare(b.name))
  }

  /**
   * 获取文件扩展名
   */
  private static getFileExtension(filename: string = ''): string {
    const parts = filename.split('.')
    return parts.length > 1 ? parts[parts.length - 1] : ''
  }

  /**
   * 检查是否为RAR或7Z文件
   */
  private static hasRar7z(name?: string): boolean {
    if (!name) return false
    const ext = this.getFileExtension(name).toLowerCase()
    return ext === 'rar' || ext === '7z'
  }
}

// 文件类型常量
export const FileTypes = {
  PDF: 'pdf',
  WORD: 'word',
  JSON: 'json',
  ZIP: 'zip',
}

// 文件路径别名
export const PathAliases = {
  PDF: {
    NORMAL: 'content_pdf',
    COMPARE: 'content_compare_pdf',
  },
  WORD: 'content_word',
  ZIP: 'content_zip_type',
}

export interface IUploadConfig {
  // 当前组件的Vue实例引用，用于获取属性和调用方法
  context: any
  // 要上传的文件列表
  fileList: any[]
  // 上传的文件类型 (pdf, word, zip)
  fileType: string
  // 检查文件是否已上传的过滤条件
  filterCondition?: (item: any, form: any) => boolean
  // 准备上传文件的回调
  beforeUpload?: (item: any) => void
  // 文件已存在时的处理
  handleExistingFile?: (item: any, path: string, form: any) => Promise<void>
  // 上传成功后的处理
  afterUpload?: (item: any, symlinkData: any, form: any) => void
  // 上传失败的处理
  onUploadFail?: (item: any, error: any) => Promise<void>
  //
  onUploadFinished?: (list: any) => void
  // 是否是PPT转希沃服务
  isPpt2Seewo?: boolean

  //
  screenForm: Function

  checkForm: Function
}

export class UploadService {
  // OSS配置
  private static async getOssConfig() {
    const auth = await getOssInfoAES()
    const { access_key: accessKeyId, access_secret: accessKeySecret } = auth

    return {
      bucket: 'hexin-worksheet',
      region: 'oss-cn-shanghai',
      key: 'files',
      accessKeyId,
      accessKeySecret,
    }
  }

  private static getPartSize(fileSize: number) {
    if (fileSize < 50 * 1024 * 1024) return 10 * 1024 * 1024      // < 50MB: 10MB
    if (fileSize < 500 * 1024 * 1024) return 50 * 1024 * 1024 // < 500MB: 50MB
    return 10 * 1024 * 1024
  }

  // 创建订单ID
  private static async ensureOrderId(context: any, fileList: any[], isPpt2Seewo?: boolean) {
    let orderId = context.order_id
    if (!orderId) {
      if (context.checkForm(context.form)) return null

      let orderData

      if (isPpt2Seewo && !context.form.alias) {
        orderData = await creatOrder({
          ...context.form,
        })
      } else {
        orderData = await creatOrder(context.form)
      }
      orderId = orderData.ticket_id
        ; (context as any).$updateQuery({ id: orderId })
      context.setOrderId(orderId)
    }
    return orderId
  }

  // 验证上传前提条件
  private static validatePrerequisites(context: any, fileType: string, fileList: any[], isPpt2Seewo?: boolean) {
    // 对于ZIP文件特殊处理
    if (fileType === FileTypes.ZIP) {
      if (!context.form.subject) {
        context.$message.error('请选择学科')
        return false
      }
      if (!context.form.stage) {
        context.$message.error('请选择学段')
        return false
      }
    }

    if (!fileList.length) {
      context.$message.warning(`请先选择一个${fileType.toUpperCase()}文件`)
      return false
    }

    if (isPpt2Seewo) {
      context.zipList = context.zipList.filter((item: any) => !item.ZIPinZIPList?.length)
    }

    return true
  }

  // 处理文件上传
  public static async uploadFiles(config: IUploadConfig) {
    const { context, fileList, fileType, isPpt2Seewo } = config

    // 确保有订单ID
    const orderId = await this.ensureOrderId(context, fileList, isPpt2Seewo)
    if (!orderId) {
      return
    }

    // 已经在上传中则返回
    if (context.uploading) return
    context.setUploading(true)

    try {
      // 获取OSS配置
      const { bucket, region, key, accessKeyId, accessKeySecret } = await this.getOssConfig()

      // 过滤出需要上传的文件
      const filesToUpload = config.filterCondition
        ? fileList.filter(item => config.filterCondition!(item, context.form))
        : fileList

      // 验证上传前提条件
      if (!this.validatePrerequisites(context, fileType, filesToUpload, config.isPpt2Seewo)) {
        context.setUploading(false)
        return
      }

      // 对每个文件进行处理
      for (const item of filesToUpload) {
        try {
          // 上传前处理
          item.status = 'uploadding'
          item.progress = 0

          if (config.beforeUpload) {
            config.beforeUpload(item)
          }

          // 检查文件是否已存在
          const material = await getMaterialFile({ md5: item.md5! })
          const { path } = material

          if (path) {
            // 文件已存在处理
            if (config.handleExistingFile) {
              await config.handleExistingFile(item, path, context.form)
            }

            item.progress = 100
            item.status = 'upload-success'
            continue
          }

          // 创建OSS客户端
          const client = new Oss({
            region,
            accessKeyId,
            accessKeySecret,
            bucket,
          })

          // 文件扩展名
          const extension = fileType === FileTypes.WORD ? 'docx' : fileType

          // 上传文件到OSS
          await client.multipartUpload(`${key}/${item.md5}.${extension}`, item[fileType], {
            parallel: 5,
            partSize: this.getPartSize(item[fileType].size),
            timeout: 4000000,
            progress: function (p: number) {
              item.progress = +(p * 100).toFixed(2)
            },
          })

          // 创建材料文件记录
          await creatMaterialFile({
            md5: item.md5!,
            name: item[fileType]!.name,
            extension,
            path: `${key}/${item.md5}.${extension}`,
          })

          // 确定路径别名
          let pathAlias
          if (fileType === FileTypes.PDF) {
            pathAlias = context.form.material.type === 3 ? PathAliases.PDF.NORMAL : PathAliases.PDF.COMPARE
          } else if (fileType === FileTypes.WORD) {
            pathAlias = PathAliases.WORD
          } else {
            pathAlias = PathAliases.ZIP
          }

          // 创建符号链接
          const symlinkData = await creatMaterialFileSymlink({
            ticket_id: context.order_id,
            origin_path: `${key}/${item.md5}.${extension}`,
            name: item[fileType]!.name,
            path_alias: pathAlias,
          })

          // 上传后处理
          if (config.afterUpload) {
            config.afterUpload(item, symlinkData, context.form)
          }

          item.status = 'upload-success'
        } catch (e) {
          // 处理失败
          if (config.onUploadFail) {
            await config.onUploadFail(item, e)
          } else {
            // 默认的错误处理
            await setUploadHistory({
              user_id: context.user.userId,
              username: context.user.nickname,
              ticket_id: context.order_id,
              type: 9999,
              ext: {
                data: `${item[fileType]!.name} 上传失败！请检查该文件的MD5值是否存在${item.md5}`,
              },
            })

            item.status = 'upload-fail'
            item.uploadInfo = e as any
            item.progress = 0
          }
        }
      }

      if (config.onUploadFinished) {
        config.onUploadFinished(filesToUpload)
      }

      // 更新订单
      await editOrder({
        ticket_id: context.order_id,
        ...config.screenForm(context.form),
      })
    } catch (error) {
      console.error(error)
    } finally {
      context.setUploading(false)
    }
  }
}

export function sizeFilter(bytesize: number) {
  let i = 0
  // 当bytesize 大于是1024字节时，开始循环，当循环到第4次时跳出；
  while (Math.abs(bytesize) >= 1024) {
    bytesize = bytesize / 1024
    i++
    if (i === 4) break
  }
  // 将Bytes,KB,MB,GB,TB定义成一维数组；
  const units = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  const newsize = bytesize.toFixed(2)
  return newsize + ' ' + units[i]
}

export function getOriginFilesName(file: string) {
  const name = file.split('/')[file.split('/').length - 1]
  const nameArr = name.split('.')
  nameArr[nameArr.length - 1] = nameArr[nameArr.length - 1].toLowerCase()
  return nameArr.join('.')
}

export function hasRar7z(name: string) {
  if (name.includes('.rar')) return 'rar'
  else if (name.includes('.7z')) return '7z'
  else ''
}
